import path from "node:path";
import {
  deleteSession,
  listSessions,
  loadSession,
  loadSessionById,
  setCurrentSession,
} from "@skaile/workspaces/runner";
import { Command } from "commander";
import pc from "picocolors";
import { logErr, logInfo, logOk, S } from "../helpers.ts";
import { makeSessionLogsCommand } from "./session-logs/index.ts";

/**
 * Create the `skaile session` command group.
 *
 * Manages the sessions stored under `.skaile/sessions/` in a project directory.
 * Subcommands: `list` (tabular view of all sessions with status and age),
 * `show <run-id>` (full JSON dump of a single session), `switch <run-id>`
 * (update the current session pointer), `delete <run-id>` (remove the session
 * file). All subcommands accept `--project-dir` to target a different directory.
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/session#make-session-command
 */
export function makeSessionCommand(): Command {
  const cmd = new Command("session").description("Manage sessions");

  cmd
    .command("list")
    .description("List all sessions for a project")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .action(async (opts) => {
      const projectDir = path.resolve(opts.projectDir);
      const sessions = await listSessions(projectDir);
      const current = await loadSession(projectDir);
      if (sessions.length === 0) {
        logInfo("No sessions found.");
        return;
      }

      console.log();
      console.log(S.heading(`  Sessions in ${projectDir}`));
      console.log();
      console.log(
        `  ${S.dim("     ")}  ${S.dim("ID".padEnd(10))}${S.dim("FLOW".padEnd(22))}${S.dim("NODES".padEnd(7))}${S.dim("LABEL".padEnd(22))}${S.dim("STATUS".padEnd(14))}${S.dim("AGE")}`,
      );
      console.log(`  ${S.dim("     ")}  ${S.rule(75)}`);
      for (const s of sessions) {
        const isCurrent = current?.runId === s.runId;
        const marker = isCurrent ? pc.cyan("  ▶  ") : "     ";
        const age = Math.round((Date.now() - new Date(s.updatedAt).getTime()) / 60000);
        const label = (s.label ?? "").padEnd(20);
        const statusColor =
          s.status === "complete"
            ? pc.green
            : s.status === "failed"
              ? pc.red
              : s.status === "running"
                ? pc.cyan
                : pc.dim;
        const status = statusColor((s.status ?? "").padEnd(12));
        console.log(
          `${marker}  ${S.dim(s.runId.slice(0, 8))}  ${s.flowId.padEnd(20)} ${label} ${status} ${S.dim(`${age}m ago`)}`,
        );
      }
      console.log();
    });

  cmd
    .command("show <run-id>")
    .description("Show a specific session")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .action(async (runId: string, opts) => {
      const projectDir = path.resolve(opts.projectDir);
      const session = await loadSessionById(projectDir, runId);
      if (!session) {
        logErr(`Session not found: ${runId}`);
        process.exit(1);
      }
      console.log(JSON.stringify(session, null, 2));
    });

  cmd
    .command("switch <run-id>")
    .description("Switch the current session")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .action(async (runId: string, opts) => {
      const projectDir = path.resolve(opts.projectDir);
      await setCurrentSession(projectDir, runId);
      logOk(`Switched to session: ${S.dim(runId.slice(0, 8))}`);
    });

  cmd
    .command("delete <run-id>")
    .description("Delete a session")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .action(async (runId: string, opts) => {
      const projectDir = path.resolve(opts.projectDir);
      await deleteSession(projectDir, runId);
      logOk(`Deleted session: ${S.dim(runId.slice(0, 8))}`);
    });

  cmd.addCommand(makeSessionLogsCommand());

  cmd.action(() => cmd.help());

  return cmd;
}
